import { useEffect, useState } from 'react';
import useApi from './useApi';
import entities from '../model/entities';

/**
 * useParent hook
 * Fetches a parent record and maps configured fields to a child entity shape.
 *
 * API:
 *   useParent(routePath, id, options?)
 *   Derives the parent entity from the segment immediately before the first ':param' in routePath,
 *   and the child entity from the segment immediately after it.
 *
 * @param {string} pathOrParent - Route path (e.g. "paymentAccount/:id/paymentTransaction")
 * @param {string} parentId - Parent primary key/id from URL params
 * @param {Object} [options]
 * @param {string} [options.childEntity] - Override child entity for transferData mapping (optional)
 * @param {boolean} [options.enabled] - When false, disables fetching. Defaults to Boolean(derivedParentEntity && parentId)
 * @returns {{success: boolean, data: Object|null, loading: boolean, error: (string|null), rawParent: (Object|null)}}
 */
export default function useParent(pathOrParent, parentId, options = {}) {
    const { childEntity: childEntityOption, enabled } = options || {};
    const api = useApi();
 
    const [state, setState] = useState({
        loading: false,
        success: false,
        data: null,
        error: null,
        rawParent: null,
    });
 
    const parentEntity = deriveParentEntity(pathOrParent);
    const derivedChildEntity = deriveChildEntity(pathOrParent);
    const effectiveChildEntity = childEntityOption ?? derivedChildEntity;
    const isEnabled = enabled !== undefined ? enabled : Boolean(parentEntity && parentId);

    useEffect(() => {
        let cancelled = false;

        const fetchParent = async () => {
            if (!isEnabled || !parentEntity || !parentId) {
                setState({
                    loading: false,
                    success: false,
                    data: null,
                    error: null,
                    rawParent: null,
                });
                return;
            }

            setState((s) => ({ ...s, loading: true, error: null }));

            try {
                const resource = `${parentEntity}/${encodeURIComponent(parentId)}`;
                const res = await api.getMe(resource);

                // Success path: getMe returns response.data (domain object)
                // Error path: getMe returns err.response (axios response with status)
                let parentRow = null;
                let error = null;

                if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'status')) {
                    // axios error response
                    if (res.status >= 200 && res.status < 300) {
                        parentRow = res.data ?? null;
                    } else {
                        error = res.statusText || `HTTP ${res.status}`;
                    }
                } else {
                    parentRow = res ?? null;
                }

                if (cancelled) return;

                if (parentRow) {
                    const mapped = mapTransfer(effectiveChildEntity, parentRow);
                    setState({
                        loading: false,
                        success: true,
                        data: mapped,
                        error: null,
                        rawParent: parentRow,
                    });
                } else {
                    setState({
                        loading: false,
                        success: false,
                        data: null,
                        error: error || 'No data',
                        rawParent: null,
                    });
                }
            } catch (e) {
                if (cancelled) return;
                setState({
                    loading: false,
                    success: false,
                    data: null,
                    error: e?.message || 'Unknown error',
                    rawParent: null,
                });
            }
        };

        fetchParent();
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentEntity, parentId, isEnabled, effectiveChildEntity]);

    return state;
}

/**
 * Derive parent entity from a route path.
 * For a path like "paymentAccount/:id/transactions", returns "paymentAccount".
 * If input looks like an entity (no '/' and no ':'), returns the input unchanged.
 * @param {string} input
 * @returns {string|null}
 */
function deriveParentEntity(input) {
    if (!input || typeof input !== 'string') return null;
    const looksLikePath = input.includes('/') || input.includes(':');
    if (!looksLikePath) return null;
 
    const parts = input.split('/');
    const idx = parts.findIndex(seg => seg.startsWith(':'));
    if (idx > 0) return parts[idx - 1] || null;
    return null;
}

/**
 * Derive child entity from a route path.
 * For a path like "paymentAccount/:id/paymentTransaction", returns "paymentTransaction".
 * If no ':param' or segment after it, returns null.
 * If input looks like an entity (no '/' and no ':'), returns null (child not derivable).
 * @param {string} input
 * @returns {string|null}
 */
function deriveChildEntity(input) {
    if (!input || typeof input !== 'string') return null;
    const looksLikePath = input.includes('/') || input.includes(':');
    if (!looksLikePath) return null;
 
    const parts = input.split('/');
    const idx = parts.findIndex(seg => seg.startsWith(':'));
    if (idx >= 0 && idx + 1 < parts.length) return parts[idx + 1] || null;
    return null;
}

/**
 * Map configured transferData from parent to child shape
 * @param {string|undefined} childEntity
 * @param {Object} parentRow
 * @returns {Object|null} mapped data object ({} if no mapping), or null when no parentRow
 */
function mapTransfer(childEntity, parentRow) {
    if (!parentRow) return null;
    if (!childEntity) return {};

    // Support API shapes that wrap the domain object in { data: {...} }
    const source =
        parentRow && typeof parentRow === 'object' && parentRow.data && typeof parentRow.data === 'object'
            ? parentRow.data
            : parentRow;

    const mapping = entities?.[childEntity]?.transferData || {};
    const out = {};

    for (const [srcKey, targetKey] of Object.entries(mapping)) {
        if (Object.prototype.hasOwnProperty.call(source, srcKey)) {
            out[targetKey] = source[srcKey];
        }
    }

   return out;
}